import React, { useCallback, useEffect, useState } from 'react';

import adminModule from '../../../api/modules/admin.module';
import { showToast } from '../../../utils/toast';
import { AdminUsersView } from './AdminUsers.view';

/**
 * Users, the container. Owns fetching, the 500ms search debounce, pagination
 * and the two write actions. `AdminUsers.view.jsx` holds everything visual and
 * is pure.
 *
 * BEHAVIOUR PRESERVED
 * -------------------
 * Same endpoint and params, same debounce interval, same reset-to-first-page on
 * a new search, same "do not refetch while the detail panel is open" guard,
 * same optimistic local update after activate and deactivate.
 *
 * TWO FIXES IN PASSING
 * --------------------
 * 1. `res.data.success` was read without a guard, so any transport failure threw
 *    a TypeError inside the fetch and the page sat on a permanent skeleton with
 *    no message. It now surfaces as a real error state with a retry.
 * 2. `total` fell back to `listings.length` read from the PREVIOUS render's
 *    state, so the page count was one page stale. It falls back to the length of
 *    the list that just arrived.
 *
 * The activate/deactivate calls were duplicated: once here and once inside
 * `UserDetailsCard`, which ran its own copy and kept its own `isActive` state.
 * Only one copy remains.
 */
const PAGE_SIZE = 10;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  /* `index` on THIS endpoint is a zero-based PAGE number, which is how the page
     has always called it. `/admin-sellers` passes an item offset for the same
     parameter name against a different endpoint. One of the two is wrong and it
     is a backend question, so both keep the convention they shipped with and
     the discrepancy is reported rather than silently harmonised. */
  const [pageIndex, setPageIndex] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selected, setSelected] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { index: pageIndex, offset: PAGE_SIZE };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const res = await adminModule.getUsersList(params);
      if (!res.success || !res.data?.success) {
        setError(new Error(res.message || 'Failed to load users'));
        return;
      }

      const payload = res.data?.details?.users || [];
      const list = Array.isArray(payload) ? payload : payload?.results || [];
      setUsers(list);
      setTotal(res.data?.details?.total || res.data?.total || list.length);
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, debouncedSearch]);

  useEffect(() => {
    if (selected) return;
    fetchUsers();
  }, [selected, fetchUsers]);

  const applyActive = (isActive) => {
    setUsers((prev) =>
      prev.map((row) => (row.id === selected.id ? { ...row, is_active: isActive } : row)),
    );
    setSelected((prev) => (prev ? { ...prev, is_active: isActive } : prev));
  };

  const handleActivate = async () => {
    if (!selected?.id) return;
    setActionBusy(true);
    try {
      const res = await adminModule.activateUser(selected.id);
      if (res.success || res?.data?.success) {
        applyActive(true);
        showToast.success(res?.message || 'User activated successfully');
      } else {
        showToast.error(res?.message || 'Failed to activate user');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selected?.id) return;
    setActionBusy(true);
    try {
      const res = await adminModule.deactivateUser(selected.id);
      if (res.success || res?.data?.success) {
        applyActive(false);
        showToast.success(res?.message || 'User deactivated successfully');
        setConfirmDeactivateOpen(false);
      } else {
        showToast.error(res?.message || 'Failed to deactivate user');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const page = pageIndex + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminUsersView
      users={users}
      loading={loading}
      error={error}
      onRetry={fetchUsers}
      search={search}
      onSearchChange={setSearch}
      page={page}
      pageCount={pageCount}
      pageSize={PAGE_SIZE}
      total={total}
      onPageChange={(next) => setPageIndex(next - 1)}
      selected={selected}
      onOpen={setSelected}
      onClose={() => setSelected(null)}
      onActivate={handleActivate}
      onDeactivate={handleDeactivate}
      actionBusy={actionBusy}
      confirmDeactivateOpen={confirmDeactivateOpen}
      onConfirmDeactivateOpen={() => setConfirmDeactivateOpen(true)}
      onConfirmDeactivateClose={() => setConfirmDeactivateOpen(false)}
    />
  );
};

export default AdminUsers;
